import React, { FormEvent, useLayoutEffect, useId, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import axiosInstance from 'config/axiosInstance';
import { newRequestId } from 'components/Driver/registerDriver';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';
import { useSaveError } from 'reusable/useSaveError';

interface UnlockLoginProps {
  id?: string;
}

/**
 * 結果が分からない失敗か（同じ X-Request-ID で送り直す）。応答が無い・5xx・408・429・401・409 処理中は、前の
 * 要求がサーバーで確定したか分からない（招待の再送の ResendInvitation と同じ考え。EV-284）。
 */
const keepsRequestId = (error: any): boolean => {
  const response = error?.response;
  if (!response) return true;
  const { status } = response;
  if (status >= 500 || status === 408 || status === 429 || status === 401) return true;
  return status === 409 && response.data?.code === 'idempotency_in_progress';
};

/**
 * BE-04 続き12（2026-10-15 PM 判断）。自社の運転者のログインのロックを解く。
 *
 * 運転者の詳細の login_locked が true のときだけ、知らせとボタンを出す。確認の後に
 * POST /v1/drivers/{id}/unlock_login/ を X-Request-ID 付きで送る。manager 自身のロックは、運用者が
 * 内部の管理サイトで解く（この画面には出ない）。
 */
const UnlockLogin: React.FC<UnlockLoginProps> = ({ id }) => {
  const { t } = useTranslation('driver');
  const { showSaveError, showMessages, saveErrorPopup } = useSaveError();
  const [locked, setLocked] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const headingId = useId();
  const close = () => setIsOpen(false);
  const dialogRef = useModalDialog(isOpen, close);
  // 完了の表示の文。null は閉じている。
  const [doneMessage, setDoneMessage] = useState<string | null>(null);
  const doneTitleId = useId();
  const closeDone = () => setDoneMessage(null);
  const doneRef = useModalDialog(doneMessage !== null, closeDone);
  // 結果の分からない失敗の後にもう一度押したときは、同じ X-Request-ID で送る。成功・結果の分かる失敗の後は捨てる。
  const requestIdRef = useRef<string | null>(null);
  // EV356。IDが同じに戻っても、前の表示/送信の結果は適用しない。
  const operationRef = useRef<{ id?: string; generation: number }>({ id, generation: 0 });

  // RV466。commit済みの対象切替/unmountで失効させ、旧passive処理の待ち時間を作らない。
  // render中にはrefを書き換えず、未commitのConcurrent描画は現在の要求を維持する。
  useLayoutEffect(() => {
    let active = true;
    operationRef.current = { id, generation: operationRef.current.generation + 1 };
    // Codex 4277021095。運転者が替わったら、取り直しを始める前に前の運転者の状態を空にする（取り直しが
    // 終わるまで・失敗したときに、前の運転者のロックの操作が見えて新しい id に送られないように）。
    setLocked(false);
    setIsOpen(false);
    setDoneMessage(null);
    setIsSubmitting(false);
    showMessages([]);
    requestIdRef.current = null;
    if (id) axiosInstance
      .get(`/v1/drivers/${id}/`)
      .then((response) => {
        if (active) setLocked(response.data?.login_locked === true);
      })
      .catch((error) => console.error('Failed to fetch driver', error));
    return () => {
      active = false;
      operationRef.current = { id: undefined, generation: operationRef.current.generation + 1 };
    };
  }, [id, showMessages]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!id || isSubmitting || operationRef.current.id !== id) return;
    const targetId = id;
    const generation = ++operationRef.current.generation;
    const isCurrent = () => operationRef.current.id === targetId && operationRef.current.generation === generation;
    setIsSubmitting(true);
    const requestId = requestIdRef.current ?? newRequestId();
    requestIdRef.current = requestId;
    try {
      const response = await axiosInstance.post(`/v1/drivers/${id}/unlock_login/`, undefined, {
        headers: { 'X-Request-ID': requestId },
      });
      if (!isCurrent()) return;
      requestIdRef.current = null;
      setIsOpen(false);
      setLocked(false);
      setDoneMessage(response?.data?.was_locked === false ? t('unlock-login.not-locked') : t('unlock-login.done'));
    } catch (error: any) {
      if (!isCurrent()) return;
      if (!keepsRequestId(error)) requestIdRef.current = null;
      showSaveError(error);
    } finally {
      if (isCurrent()) setIsSubmitting(false);
    }
  };

  return (
    <div>
      {locked ? (
        <div className="flex items-center gap-4">
          <p className="text-body-2 text-error">{t('unlock-login.notice')}</p>
          <button
            type="button"
            className="inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300 h-10 px-4"
            onClick={() => setIsOpen(true)}
          >
            {t('unlock-login.button')}
          </button>
        </div>
      ) : null}
      {isOpen ? inModalLayer(
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby={headingId}
              ref={dialogRef}
              tabIndex={-1}
            >
              <h3 className="text-subtitle-1 font-medium text-neutral/500 mt-4 mx-6" id={headingId}>
                {t('unlock-login.button')}
              </h3>
              <hr className="border-neutral/50 mt-4 w-full" />
              <form onSubmit={handleSubmit}>
                <p className="m-4 sm:m-6 text-body-2">{t('unlock-login.confirm')}</p>
                <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    className="w-full inline-flex justify-center items-center rounded-md border bg-white text-subtitle-2 text-neutral/100 disabled:opacity-60"
                    onClick={close}
                  >
                    {t('ui.cancel', { ns: 'common' })}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12 disabled:opacity-60"
                  >
                    {t('unlock-login.button')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
      {doneMessage !== null ? inModalLayer(
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div
            className="flex flex-col py-4 bg-white rounded-md shadow-lg w-1/3"
            role="dialog"
            aria-modal="true"
            aria-labelledby={doneTitleId}
            ref={doneRef}
            tabIndex={-1}
          >
            <div className="flex justify-start px-4 pb-4">
              <h4 id={doneTitleId} className="text-title">{t('ui.success', { ns: 'common' })}</h4>
            </div>
            <div className="border-y w-full flex flex-col items-center justify-center py-4">
              <CheckIcon className="bg-[#67B926] rounded-full text-white h-24 p-4" />
              <p className="py-4 px-4 text-center">{doneMessage}</p>
            </div>
            <div className="flex justify-end mt-4 w-full px-4">
              <button type="button" className="bg-primary/300 text-white w-full py-2 rounded-md" onClick={closeDone}>
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {saveErrorPopup}
    </div>
  );
};

export default UnlockLogin;
